import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { API } from "@/lib/api";
import { FileXls, FilePdf, FileCsv, Calendar, X, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const REPORTS = [
  { key: "batches", title: "Production Batches", desc: "All batches with yield, moisture, stages & QC status" },
  { key: "inventory", title: "Raw Material Inventory", desc: "Stock, min-stock, rate, supplier & expiry" },
  { key: "finished-goods", title: "Finished Goods", desc: "SKU-wise packed inventory with mfg/exp dates & value" },
  { key: "qc", title: "Quality Control Log", desc: "Per-stage QC records with pass/fail & CAPA notes" },
  { key: "sales", title: "Sales & Dispatch", desc: "Sales orders with dispatch status & courier tracking" },
  { key: "purchase", title: "Purchase Orders", desc: "PO history with supplier & received status" },
];

const FMT = [
  { ext: "xlsx", label: "Excel", Icon: FileXls, testid: "xlsx" },
  { ext: "pdf",  label: "PDF",   Icon: FilePdf, testid: "pdf" },
  { ext: "csv",  label: "CSV",   Icon: FileCsv, testid: "csv" },
];

// Date range presets
const PRESETS = [
  { label: "Today",       days: 0 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days",days: 30 },
  { label: "Last 90 days",days: 90 },
  { label: "This year",   thisYear: true },
];

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return d.toISOString().slice(0, 10);
}
function yearStartISO() { return new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10); }

async function download(key, ext, from, to) {
  const token = localStorage.getItem("chericious_token");
  const params = new URLSearchParams();
  if (from) params.set("from_date", from);
  if (to)   params.set("to_date", to);
  const qs = params.toString();
  const url = `${API}/reports/${key}.${ext}${qs ? `?${qs}` : ""}`;
  try {
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const blob = await res.blob();
    const suffix = from || to ? `_${from || "all"}_to_${to || "all"}` : "";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${key}${suffix}.${ext}`;
    a.click();
    toast.success(`${key}${suffix}.${ext} downloaded`);
  } catch (e) {
    toast.error(`Download failed: ${e.message}`);
  }
}

export default function Reports() {
  const [active, setActive] = useState("batches");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyPreset = (p) => {
    setTo(todayISO());
    if (p.thisYear) setFrom(yearStartISO());
    else            setFrom(todayISO(p.days));
  };
  const clear = () => { setFrom(""); setTo(""); };

  const loadPreview = async () => {
    setLoading(true);
    try {
      const params = { limit: 25 };
      if (from) params.from_date = from;
      if (to)   params.to_date = to;
      const { data } = await api.get(`/reports/${active}/preview`, { params });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setPreview(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [active, from, to]);

  const activeReport = REPORTS.find(r => r.key === active);

  return (
    <div data-testid="reports-page">
      <PageHeader subtitle="Analytics" title="Reports & Exports"/>

      {/* Report selector cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {REPORTS.map(r => (
          <Card key={r.key}
                className={`shadow-none cursor-pointer transition-colors ${active === r.key ? "border-primary border-2" : "hover:border-primary/50"}`}
                onClick={() => setActive(r.key)}
                data-testid={`report-select-${r.key}`}>
            <CardContent className="p-4">
              <div className="text-xs font-bold uppercase tracking-wide">{r.title}</div>
              <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{r.desc}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Date range + export */}
      <Card className="shadow-none mb-4">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="grow min-w-[240px]">
              <Label className="text-xs mb-2 block flex items-center gap-1.5"><Calendar size={12}/> Date range</Label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map(p => (
                  <Button key={p.label} size="sm" variant="outline" onClick={() => applyPreset(p)}
                           data-testid={`preset-${p.label.replace(/\s/g, '-').toLowerCase()}`}>
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="reports-from"/>
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="reports-to"/>
            </div>
            {(from || to) && (
              <Button size="sm" variant="ghost" onClick={clear} data-testid="reports-clear-range">
                <X size={14} className="mr-1"/>Clear range
              </Button>
            )}
            <div className="grow"/>
            <div className="flex gap-2">
              {FMT.map(f => (
                <Button key={f.ext} onClick={() => download(active, f.ext, from, to)}
                         data-testid={`report-${active}-${f.testid}`}>
                  <f.Icon size={14} className="mr-1"/>Download {f.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <div className="flex justify-between items-center px-4 pt-4 pb-3">
            <div>
              <div className="text-sm font-bold flex items-center gap-2"><Eye size={14}/> Preview — {activeReport?.title}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {loading ? "Loading…" : preview ? `${preview.rows.length} of ${preview.total.toLocaleString()} rows shown${from || to ? ` · ${from || "start"} → ${to || "now"}` : ""}` : "No data"}
              </div>
            </div>
            {preview?.total > 0 && <Badge variant="secondary" className="mono">{preview.total.toLocaleString()} total</Badge>}
          </div>
          {preview?.rows?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>{preview.headers.map(h => <TableHead key={h} className="text-xs">{h}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`preview-row-${i}`}>
                    {preview.headers.map(h => (
                      <TableCell key={h} className="text-xs mono text-muted-foreground max-w-[200px] truncate">
                        {formatCell(r[h])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : !loading && (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No rows in this range. Adjust the date filter above and try again.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatCell(v) {
  if (v == null) return "—";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  const s = String(v);
  // Trim ISO timestamps to date + time
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.replace("T", " ").slice(0, 16);
  return s;
}
